import React from 'react';
import { Grid } from '@material-ui/core';
import { Margin, WhiteSection } from 'components/base/layout-components';
import CycleTime from './components/CycleTime';
import WorkBreakdown from './components/WorkBreakdown';
import ErrorBoundary from 'components/ErrorBoundary';
import ContributorsRosterByTeam from 'components/ContributorsRosterByTeam/ContributorsRosterByTeam';
import RetrospectReviewDepth from './components/RetrospectReviewDepth';
import { getWorkBreakdownConfigurations } from './util';
import { TRACKING } from 'common/constants/constants';
import * as TrackingService from 'common/services/tracking.service';
import {
  getCycleTimeConfigurations,
  goToBranchesOrderBy
} from 'views/root/views/home/views/dashboard/dashboard.service';
import { SPACES } from 'common/constants/sizes';
import RetrospectDeploymentFrequency from './components/RetrospectDeploymentFrequency';
import { metricNames } from '@linearb/linjs-common';
import ContributorsRosterByTeamHidePersonalInfo from 'components/ContributorsRosterByTeamHidePersonalInfo/ContributorsRosterByTeamHidePersonalInfo';
import { useHistory } from 'react-router';

const TeamDashboardPreviousIteration = props => {
  const {
    cycleTimeGridSize,
    workBreakdownTrendData,
    teamWorkBreakdownData,
    cycleTimeTrendData,
    cycleTimeAverage,
    totalCycleTimeInMinutes,
    pipelineMilestones,
    orgWorkBreakdownData,
    teamRetrospectData,
    deploymentFreqData,
    noReleasesInIterations,
    contributorsOrder,
    setContributorsOrder,
    contributorsMetrics,
    teamMetrics,
    reviewsTrendData,
    isVPMode,
    selectedTeam,
    isFinishedOnBoarding,
    contributors,
    isRetrospect,
    toggleModal,
    uiState,
    currentIteration,
    currentTeams,
    toggleSnackBar,
    updateTeam,
    updateTeamContributors,
    getTeams,
    myAccount,
    shouldHidePersonalInformationDashboard
  } = props;
  const history = useHistory();
  const { dates, selectedTeamId } = uiState;
  const styleAexs = { gridLineWidth: 0 };
  const chartColor = 'malachite';
  return (
    <>
      {isFinishedOnBoarding && (
        <>
          <Grid item xs={cycleTimeGridSize}>
            <Margin r='small' b='small'>
              <WhiteSection
                display='flex'
                borderRadius='0.45rem'
                height='22rem'
              >
                <ErrorBoundary componentName='Cycle Time'>
                  <CycleTime
                    toggleModal={toggleModal}
                    value={totalCycleTimeInMinutes}
                    trendData={cycleTimeTrendData}
                    selectedTeamId={selectedTeamId}
                    onLinkIconClick={() => {
                      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                        page: TRACKING.PAGES.DASHBOARD,
                        click_type: TRACKING.CLICK.CYCLE_TIME_CONTEXT
                      });
                      goToBranchesOrderBy(
                        'cycle_time,desc',
                        uiState,
                        selectedTeam,
                        history,
                        metricNames.CYCLE_TIME,
                        'cycle_time_updated'
                      );
                    }}
                    configurations={getCycleTimeConfigurations(
                      pipelineMilestones
                    )}
                    avgTime={cycleTimeAverage}
                    dates={dates}
                  />
                </ErrorBoundary>
              </WhiteSection>
            </Margin>
          </Grid>
          <Grid item xs={12 - cycleTimeGridSize}>
            <WhiteSection borderRadius='0.45rem' height='22rem'>
              <ErrorBoundary componentName='Work Breakdown'>
                <WorkBreakdown
                  trendData={workBreakdownTrendData}
                  onLinkIconClick={() => {
                    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                      page: TRACKING.PAGES.DASHBOARD,
                      click_type: TRACKING.CLICK.WORK_BREAKDOWN_CONTEXT
                    });
                    goToBranchesOrderBy(
                      'code_changes,desc',
                      uiState,
                      selectedTeam,
                      history
                    );
                  }}
                  configurations={getWorkBreakdownConfigurations(
                    isVPMode ? orgWorkBreakdownData : teamWorkBreakdownData
                  )}
                  headLine={{
                    value: (isVPMode
                      ? orgWorkBreakdownData
                      : teamWorkBreakdownData
                    ).total,
                    description: 'CODE CHANGES'
                  }}
                />
              </ErrorBoundary>
            </WhiteSection>
          </Grid>
        </>
      )}
      {shouldHidePersonalInformationDashboard ? (
        <>
          <Grid item xs={4}>
            <Margin r='small'>
              <WhiteSection
                borderRadius='0.45rem'
                height={isFinishedOnBoarding ? '51rem' : '73rem'}
              >
                <ErrorBoundary componentName='Team Members'>
                  <ContributorsRosterByTeamHidePersonalInfo
                    teams={currentTeams}
                    getTeams={getTeams}
                    updateTeam={updateTeam}
                    updateTeamContributors={updateTeamContributors}
                    selectedTeam={selectedTeam}
                    toggleModal={toggleModal}
                    toggleSnackBar={toggleSnackBar}
                    order={contributorsOrder}
                    contributorsMetrics={contributorsMetrics}
                    teamMetrics={teamMetrics}
                    onOrderChange={order => setContributorsOrder(order)}
                    isRetrospect={isRetrospect}
                    contributors={contributors}
                    currentIteration={currentIteration}
                    myAccount={myAccount}
                    isFinishedOnBoarding={isFinishedOnBoarding}
                  />
                </ErrorBoundary>
              </WhiteSection>
            </Margin>
          </Grid>
          <Grid item xs={8}>
            <Margin b='small'>
              <WhiteSection
                borderRadius='0.45rem'
                height='25rem'
                r={SPACES.medium}
                l={SPACES.medium}
                b={SPACES.small}
              >
                <ErrorBoundary componentName='Review Depth'>
                  <RetrospectReviewDepth
                    reviews={teamMetrics[metricNames.REVIEWS] || 0}
                    avgComments={teamMetrics[metricNames.REVIEW_DEPTH] || 0}
                    data={teamRetrospectData.reviewDepth}
                    trendData={reviewsTrendData}
                    styleAexs={styleAexs}
                    chartColor={chartColor}
                  />
                </ErrorBoundary>
              </WhiteSection>
            </Margin>
            <WhiteSection
              borderRadius='0.45rem'
              height='25rem'
              r={SPACES.medium}
              l={SPACES.medium}
              t={SPACES.small}
              b={SPACES.small}
            >
              <ErrorBoundary componentName='Deployment Frequency'>
                <RetrospectDeploymentFrequency
                  releases={teamMetrics[metricNames.RELEASES_COUNT] || 0}
                  data={teamRetrospectData.deploymentFrequency}
                  trendData={deploymentFreqData}
                  noReleasesInIterations={noReleasesInIterations}
                  history={history}
                  styleAexs={styleAexs}
                  chartColor={chartColor}
                />
              </ErrorBoundary>
            </WhiteSection>
          </Grid>
        </>
      ) : (
        <>
          <Grid item xs={8}>
            <Margin r='small'>
              <WhiteSection
                borderRadius='0.45rem'
                height={isFinishedOnBoarding ? '51rem' : '73rem'}
                padding='1rem'
              >
                <ErrorBoundary componentName='Team Members'>
                  <ContributorsRosterByTeam
                    teams={currentTeams}
                    getTeams={getTeams}
                    updateTeam={updateTeam}
                    updateTeamContributors={updateTeamContributors}
                    selectedTeam={selectedTeam}
                    toggleModal={toggleModal}
                    toggleSnackBar={toggleSnackBar}
                    order={contributorsOrder}
                    contributorsMetrics={contributorsMetrics}
                    teamMetrics={teamMetrics}
                    onOrderChange={order => setContributorsOrder(order)}
                    isRetrospect={isRetrospect}
                    contributors={contributors}
                    currentIteration={currentIteration}
                    myAccount={myAccount}
                    isFinishedOnBoarding={isFinishedOnBoarding}
                  />
                </ErrorBoundary>
              </WhiteSection>
            </Margin>
          </Grid>

          <Grid item xs={4}>
            <Margin b='small'>
              <WhiteSection
                borderRadius='0.45rem'
                height='25rem'
                r={SPACES.medium}
                l={SPACES.medium}
                t={SPACES.small}
                b={SPACES.small}
              >
                <ErrorBoundary componentName='Review Depth'>
                  <RetrospectReviewDepth
                    reviews={teamMetrics[metricNames.REVIEWS] || 0}
                    avgComments={teamMetrics[metricNames.REVIEW_DEPTH] || 0}
                    data={teamRetrospectData.reviewDepth}
                    trendData={reviewsTrendData}
                  />
                </ErrorBoundary>
              </WhiteSection>
            </Margin>
            <WhiteSection
              borderRadius='0.45rem'
              height='25rem'
              r={SPACES.medium}
              l={SPACES.medium}
              t={SPACES.small}
              b={SPACES.small}
            >
              <ErrorBoundary componentName='Deployment Frequency'>
                <RetrospectDeploymentFrequency
                  releases={teamMetrics[metricNames.RELEASES_COUNT] || 0}
                  data={teamRetrospectData.deploymentFrequency}
                  trendData={deploymentFreqData}
                  noReleasesInIterations={noReleasesInIterations}
                  history={history}
                />
              </ErrorBoundary>
            </WhiteSection>
          </Grid>
        </>
      )}
    </>
  );
};

export default TeamDashboardPreviousIteration;
